import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase, Reservation, Review, Program } from '../../lib/supabase';

export default function UserDashboard() {
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<'reservations' | 'reviews' | 'programs'>('reservations');
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    if (!user) return;

    try {
      // 예약 내역 조회
      const { data: reservationData } = await supabase
        .from('reservations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      // 리뷰 내역 조회
      const { data: reviewData } = await supabase
        .from('reviews')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      // 프로그램 참가 이력 조회
      const { data: programData } = await supabase
        .from('programs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      setReservations(reservationData || []);
      setReviews(reviewData || []);
      setPrograms(programData || []);
    } catch (error) {
      console.error('데이터 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      case 'completed': return 'bg-blue-100 text-blue-700';
      case 'upcoming': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return '확정';
      case 'pending': return '대기중';
      case 'cancelled': return '취소됨';
      case 'completed': return '완료';
      case 'upcoming': return '예정';
      default: return status;
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i
        key={i}
        className={`ri-star-${i < rating ? 'fill' : 'line'} text-yellow-400`}
      ></i>
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-4xl text-green-500 mb-4"></i>
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* 헤더 */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">마이페이지</h1>
              <p className="text-gray-600">안녕하세요, {user?.user_metadata?.name || user?.email}님!</p>
            </div>
            <button
              onClick={signOut}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-line mr-2"></i>
              로그아웃
            </button>
          </div>
        </div>

        {/* 탭 네비게이션 */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('reservations')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'reservations'
                  ? 'bg-green-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-calendar-line mr-2"></i>
              예약 내역 ({reservations.length})
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'reviews'
                  ? 'bg-green-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-star-line mr-2"></i>
              내 리뷰 ({reviews.length})
            </button>
            <button
              onClick={() => setActiveTab('programs')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'programs'
                  ? 'bg-green-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-trophy-line mr-2"></i>
              참가 이력 ({programs.length})
            </button>
          </div>

          <div className="p-8">
            {/* 예약 내역 */}
            {activeTab === 'reservations' && (
              <div className="space-y-6">
                {reservations.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-calendar-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500 text-lg">예약 내역이 없습니다.</p>
                  </div>
                ) : (
                  reservations.map((reservation) => (
                    <div key={reservation.id} className="bg-gray-50 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-gray-800 mb-2">
                            {reservation.partner_name}
                          </h3>
                          <div className="space-y-1 text-gray-600">
                            <p><i className="ri-calendar-line mr-2"></i>{reservation.date}</p>
                            <p><i className="ri-time-line mr-2"></i>{reservation.time}</p>
                            <p><i className="ri-group-line mr-2"></i>{reservation.participants}명</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(reservation.status)}`}>
                          {getStatusText(reservation.status)}
                        </span>
                      </div>
                      {reservation.message && (
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-gray-600 text-sm">{reservation.message}</p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* 리뷰 내역 */}
            {activeTab === 'reviews' && (
              <div className="space-y-6">
                {reviews.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-star-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500 text-lg">작성한 리뷰가 없습니다.</p>
                  </div>
                ) : (
                  reviews.map((review) => (
                    <div key={review.id} className="bg-gray-50 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-gray-800 mb-2">
                            {review.partner_name}
                          </h3>
                          <div className="flex items-center gap-2 mb-2">
                            {renderStars(review.rating)}
                            <span className="text-gray-600 text-sm">
                              {new Date(review.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-gray-700">{review.comment}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* 프로그램 참가 이력 */}
            {activeTab === 'programs' && (
              <div className="space-y-6">
                {programs.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-trophy-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500 text-lg">참가 이력이 없습니다.</p>
                  </div>
                ) : (
                  programs.map((program) => (
                    <div key={program.id} className="bg-gray-50 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-gray-800 mb-2">
                            {program.partner_name}
                          </h3>
                          <div className="space-y-1 text-gray-600">
                            <p><i className="ri-bookmark-line mr-2"></i>{program.program_type}</p>
                            <p><i className="ri-calendar-line mr-2"></i>{program.date}</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(program.status)}`}>
                          {getStatusText(program.status)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}